import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import AdminService from "../services/AdminService";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icon issue with webpack/vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const MOCK_COUNTRIES = ["Saint Kitts And Nevis", "Trinidad And Tobago", "Jamaica", "Barbados", "Bahamas", "Grenada"];
const DEFAULT_CENTER = [17.35, -62.78];

// Draggable marker component
const DraggableMarker = ({ position, onDragEnd }) => {
    const markerRef = useRef(null);
    const eventHandlers = {
        dragend() {
            const marker = markerRef.current;
            if (marker) {
                const latlng = marker.getLatLng();
                onDragEnd(latlng.lat, latlng.lng);
            }
        },
    };
    return <Marker draggable={true} eventHandlers={eventHandlers} position={position} ref={markerRef} />;
};

// Component to recenter map when position changes
const MapUpdater = ({ center }) => {
    const map = useMapEvents({});
    useEffect(() => {
        if (center && center[0] && center[1]) {
            map.setView(center, map.getZoom());
        }
    }, [center, map]);
    return null;
};

const AdAddPage = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const locationSearchTimeout = useRef(null);

    const [form, setForm] = useState({
        title: "", description: "", slug: "", categoryId: "", subCategoryId: "",
        price: "", contactName: "", contactPhone: "", hideMobile: false,
        state: "", city: "", country: "", lat: "", lng: "", location: "",
        countryId: "", stateId: "", cityId: "",
        countryList: [], featured: false, status: true, expireDate: "",
    });

    const [categories, setCategories] = useState([]);
    const [locationCountries, setLocationCountries] = useState([]);
    const [locationStates, setLocationStates] = useState([]);
    const [locationCities, setLocationCities] = useState([]);
    const [images, setImages] = useState([]);
    const [errors, setErrors] = useState({});
    const [dragOver, setDragOver] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mapPosition, setMapPosition] = useState(DEFAULT_CENTER);

    // Fetch categories from API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await AdminService.getCategories();
                const data = res.data?.data || res.data;
                if (Array.isArray(data)) setCategories(data);
            } catch (err) {
                console.warn("Could not fetch categories, using fallback");
                setCategories([
                    { id: 1, name: "Electronics", subCategories: [{ id: 11, name: "Mobile Phones" }, { id: 12, name: "Laptops" }] },
                    { id: 2, name: "Vehicles", subCategories: [{ id: 21, name: "Cars" }, { id: 22, name: "Trucks" }] },
                    { id: 3, name: "Real Estate", subCategories: [{ id: 31, name: "Apartments" }, { id: 32, name: "Houses" }] },
                ]);
            }
        };
        fetchCategories();
    }, []);

    // Fetch location countries
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await AdminService.getLocationCountries();
                const responseData = res.data?.data || res.data;
                const countries = responseData?.country || responseData;
                if (Array.isArray(countries)) setLocationCountries(countries);
            } catch (err) {
                console.error("Failed to fetch countries:", err);
            }
        };
        fetchCountries();
    }, []);

    const fetchStatesForCountry = async (countryId) => {
        if (!countryId) { setLocationStates([]); setLocationCities([]); return; }
        try {
            const selectedCountry = locationCountries.find((c) => c.id === parseInt(countryId));
            if (selectedCountry) {
                const res = await AdminService.getStatesByCca2(selectedCountry.cca2);
                const data = res.data?.data || res.data;
                if (Array.isArray(data)) setLocationStates(data);
                else setLocationStates([]);
            }
        } catch (err) { console.error("Failed to fetch states:", err); setLocationStates([]); }
        setLocationCities([]);
    };

    const fetchCitiesForState = async (stateId) => {
        if (!stateId) { setLocationCities([]); return; }
        try {
            const res = await AdminService.getCitiesByStateId(stateId);
            const data = res.data?.data || res.data;
            if (Array.isArray(data)) setLocationCities(data);
            else setLocationCities([]);
        } catch (err) { console.error("Failed to fetch cities:", err); setLocationCities([]); }
    };

    // Find subcategories for selected category
    const getSubcategories = () => {
        const cat = categories.find((c) => c.id === parseInt(form.categoryId));
        return cat?.subCategories || cat?.subcategories || [];
    };

    const handleChange = (field, value) => {
        const updates = { [field]: value };
        if (field === "title") {
            updates.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
        }
        if (field === "categoryId") {
            updates.subCategoryId = "";
        }
        if (field === "countryId") {
            const selectedCountry = locationCountries.find((c) => c.id === parseInt(value));
            updates.country = selectedCountry ? selectedCountry.name : "";
            updates.stateId = ""; updates.state = ""; updates.cityId = ""; updates.city = "";
            fetchStatesForCountry(value);
        }
        if (field === "stateId") {
            const selectedState = locationStates.find((s) => s.id === parseInt(value));
            updates.state = selectedState ? selectedState.name : "";
            updates.cityId = ""; updates.city = "";
            fetchCitiesForState(value);
        }
        if (field === "cityId") {
            const selectedCity = locationCities.find((c) => c.id === parseInt(value));
            updates.city = selectedCity ? selectedCity.name : "";
        }
        setForm((prev) => ({ ...prev, ...updates }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    };

    const handleLocationSearch = (value) => {
        setForm((prev) => ({ ...prev, location: value }));
        if (errors.location) setErrors((prev) => ({ ...prev, location: null }));
        if (locationSearchTimeout.current) clearTimeout(locationSearchTimeout.current);
        if (!value.trim()) return;

        locationSearchTimeout.current = setTimeout(async () => {
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=1`);
                const results = await response.json();
                if (results && results.length > 0) {
                    const { lat, lon } = results[0];
                    const newLat = parseFloat(lat).toFixed(6);
                    const newLng = parseFloat(lon).toFixed(6);
                    setForm((prev) => ({ ...prev, lat: newLat, lng: newLng }));
                    setMapPosition([parseFloat(lat), parseFloat(lon)]);
                }
            } catch (err) { console.error("Geocoding failed:", err); }
        }, 800);
    };

    const handleMarkerDragEnd = (lat, lng) => {
        const newLat = lat.toFixed(6);
        const newLng = lng.toFixed(6);
        setForm((prev) => ({ ...prev, lat: newLat, lng: newLng }));
        setMapPosition([lat, lng]);
    };

    const handleCountryToggle = (country) => {
        setForm((prev) => ({
            ...prev,
            countryList: prev.countryList.includes(country)
                ? prev.countryList.filter((c) => c !== country)
                : [...prev.countryList, country],
        }));
    };

    // Image handling
    const handleFiles = (fileList) => {
        if (images.length + fileList.length > 6) { toast.error("Maximum 6 images allowed"); return; }
        const files = Array.from(fileList).filter((f) => {
            if (!f.type.startsWith("image/")) { toast.error(`"${f.name}" is not an image`); return false; }
            if (f.size > 10 * 1024 * 1024) { toast.error(`"${f.name}" exceeds 10MB`); return false; }
            return true;
        });
        const newImgs = files.map((file) => ({ file, preview: URL.createObjectURL(file), name: file.name }));
        setImages((prev) => [...prev, ...newImgs]);
    };

    const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };

    const removeImage = (index) => {
        setImages((prev) => {
            const updated = [...prev];
            URL.revokeObjectURL(updated[index].preview);
            updated.splice(index, 1);
            return updated;
        });
    };

    const validate = () => {
        const err = {};
        if (!form.title.trim()) err.title = "Title is required";
        if (!form.description.trim()) err.description = "Description is required";
        if (!form.subCategoryId) err.subCategoryId = "Select a subcategory";
        if (!form.contactName.trim()) err.contactName = "Contact name is required";
        if (!form.contactPhone.trim()) err.contactPhone = "Contact phone is required";
        if (!form.countryId) err.countryId = "Country is required";
        if (!form.stateId) err.stateId = "State is required";
        if (form.countryList.length === 0) err.countryList = "Select at least one country";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = async (e, isDraft = false) => {
        if (e) e.preventDefault();
        if (!isDraft && !validate()) { toast.error("Please fix validation errors"); return; }

        setSaving(true);
        try {
            const payload = {
                title: form.title,
                description: form.description,
                slug: form.slug,
                subCategoryId: form.subCategoryId ? parseInt(form.subCategoryId) : null,
                price: form.price ? parseFloat(form.price) : null,
                contactName: form.contactName,
                contactPhone: form.contactPhone,
                hide: form.hideMobile,
                state: form.state,
                city: form.city,
                country: form.country,
                lat: form.lat ? parseFloat(form.lat) : null,
                lng: form.lng ? parseFloat(form.lng) : null,
                location: form.location,
                countryList: form.countryList,
                featured: form.featured,
            };

            await AdminService.createAd(payload);
            toast.success(isDraft ? "Ad saved as draft!" : "Ad published successfully!");
            navigate("/admin/ads");
        } catch (err) {
            console.error("Failed to create ad:", err);
            toast.error("Failed to create ad: " + (err.response?.data?.message || err.message));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="admin-breadcrumb">
                <Link to="/admin/ads">Ads</Link>
                <i className="fa fa-angle-right" />
                <span>Add New Ad</span>
            </div>

            <div className="admin-page-header">
                <h2>Create New Ad</h2>
                <div className="admin-page-actions">
                    <button className="admin-btn admin-btn-outline" onClick={() => handleSubmit(null, true)} disabled={saving}>
                        <i className="fa fa-save" /> Save as Draft
                    </button>
                    <button className="admin-btn admin-btn-primary" onClick={handleSubmit} disabled={saving}>
                        {saving ? <><i className="fa fa-spinner fa-spin" /> Saving...</> : <><i className="fa fa-paper-plane" /> Publish Ad</>}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
                    {/* Left Column */}
                    <div>
                        {/* Basic Info */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-info-circle" /> Basic Information</div>
                                <div className="admin-form-group">
                                    <label>Title *</label>
                                    <input type="text" className="admin-form-control" value={form.title}
                                        onChange={(e) => handleChange("title", e.target.value)} maxLength={100}
                                        placeholder="Enter ad title..." />
                                    {errors.title && <div className="admin-form-error">{errors.title}</div>}
                                    <div className="admin-form-helper">{form.title.length}/100</div>
                                </div>
                                <div className="admin-form-group">
                                    <label>Slug</label>
                                    <input type="text" className="admin-form-control" value={form.slug}
                                        onChange={(e) => handleChange("slug", e.target.value)}
                                        placeholder="auto-generated-from-title" />
                                </div>
                                <div className="admin-form-group">
                                    <label>Description *</label>
                                    <textarea className="admin-form-control" rows="6" value={form.description}
                                        onChange={(e) => handleChange("description", e.target.value)} maxLength={5000}
                                        placeholder="Describe the item in detail..."
                                        style={{ resize: "vertical" }} />
                                    {errors.description && <div className="admin-form-error">{errors.description}</div>}
                                    <div className="admin-form-helper">{form.description.length}/5000</div>
                                </div>
                            </div>
                        </div>

                        {/* Category */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-th-large" /> Category</div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>Category *</label>
                                        <select className="admin-form-control" value={form.categoryId}
                                            onChange={(e) => handleChange("categoryId", e.target.value)}>
                                            <option value="">Select Category</option>
                                            {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Subcategory *</label>
                                        <select className="admin-form-control" value={form.subCategoryId}
                                            onChange={(e) => handleChange("subCategoryId", e.target.value)}
                                            disabled={!form.categoryId}>
                                            <option value="">Select Subcategory</option>
                                            {getSubcategories().map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                                        </select>
                                        {errors.subCategoryId && <div className="admin-form-error">{errors.subCategoryId}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Pricing */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-usd" /> Pricing</div>
                                <div className="admin-form-group">
                                    <label>Price (USD)</label>
                                    <input type="number" className="admin-form-control" value={form.price}
                                        onChange={(e) => handleChange("price", e.target.value)}
                                        min="0" step="0.01" placeholder="0.00" />
                                </div>
                            </div>
                        </div>

                        {/* Media Upload */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-image" /> Media</div>
                                <div className={`admin-upload-zone ${dragOver ? "dragover" : ""}`}
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}>
                                    <i className="fa fa-cloud-upload" />
                                    <p>Drop images here or <span>browse</span></p>
                                    <small>Max 6 images · 10MB each · JPG, PNG, WebP</small>
                                    <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                                        onChange={(e) => handleFiles(e.target.files)} />
                                </div>
                                {images.length > 0 && (
                                    <div className="admin-upload-preview">
                                        {images.map((img, i) => (
                                            <div key={i} className="admin-upload-preview-item">
                                                <img src={img.preview} alt={img.name} />
                                                <button type="button" className="admin-upload-preview-remove" onClick={() => removeImage(i)}>
                                                    <i className="fa fa-times" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Contact */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-address-card" /> Contact</div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>Contact Name *</label>
                                        <input type="text" className="admin-form-control" value={form.contactName}
                                            onChange={(e) => handleChange("contactName", e.target.value)}
                                            maxLength={65} placeholder="John Doe" />
                                        {errors.contactName && <div className="admin-form-error">{errors.contactName}</div>}
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Contact Phone *</label>
                                        <input type="text" className="admin-form-control" value={form.contactPhone}
                                            onChange={(e) => handleChange("contactPhone", e.target.value)}
                                            placeholder="+1 868-555-0101" />
                                        {errors.contactPhone && <div className="admin-form-error">{errors.contactPhone}</div>}
                                    </div>
                                </div>
                                <div className="admin-form-group">
                                    <div className="admin-toggle-group">
                                        <label className="admin-toggle">
                                            <input type="checkbox" checked={form.hideMobile}
                                                onChange={(e) => handleChange("hideMobile", e.target.checked)} />
                                            <span className="admin-toggle-slider" />
                                        </label>
                                        <span className="admin-toggle-label">Hide mobile number from public</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Location */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-map-marker" /> Location</div>

                                {/* Map Selection */}
                                <div className="admin-form-group">
                                    <label>Pin Location on Map</label>
                                    <div style={{ height: 350, width: "100%", borderRadius: 8, overflow: "hidden", border: "1px solid #ddd", marginBottom: 15 }}>
                                        <MapContainer center={mapPosition} zoom={7} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            <DraggableMarker position={mapPosition} onDragEnd={handleMarkerDragEnd} />
                                            <MapUpdater center={mapPosition} />
                                        </MapContainer>
                                    </div>
                                    <small className="admin-form-helper">Drag the marker to the exact location or search below.</small>
                                </div>

                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>Country *</label>
                                        <select className="admin-form-control" value={form.countryId}
                                            onChange={(e) => handleChange("countryId", e.target.value)}>
                                            <option value="">Select Country</option>
                                            {locationCountries.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                                        </select>
                                        {errors.countryId && <div className="admin-form-error">{errors.countryId}</div>}
                                    </div>
                                    <div className="admin-form-group">
                                        <label>State *</label>
                                        <select className="admin-form-control" value={form.stateId}
                                            onChange={(e) => handleChange("stateId", e.target.value)}
                                            disabled={!form.countryId}>
                                            <option value="">Select State</option>
                                            {locationStates.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                                        </select>
                                        {errors.stateId && <div className="admin-form-error">{errors.stateId}</div>}
                                    </div>
                                </div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>City</label>
                                        <select className="admin-form-control" value={form.cityId}
                                            onChange={(e) => handleChange("cityId", e.target.value)}
                                            disabled={!form.stateId}>
                                            <option value="">Select City</option>
                                            {locationCities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                                        </select>
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Location Search / Text</label>
                                        <input type="text" className="admin-form-control" value={form.location}
                                            onChange={(e) => handleLocationSearch(e.target.value)}
                                            placeholder="Type address to search on map..." />
                                    </div>
                                </div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>Latitude</label>
                                        <input type="number" className="admin-form-control" value={form.lat}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setForm(prev => ({ ...prev, lat: val }));
                                                if (val && !isNaN(val)) setMapPosition([parseFloat(val), parseFloat(form.lng || DEFAULT_CENTER[1])]);
                                            }} step="any" placeholder="10.6568" />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Longitude</label>
                                        <input type="number" className="admin-form-control" value={form.lng}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setForm(prev => ({ ...prev, lng: val }));
                                                if (val && !isNaN(val)) setMapPosition([parseFloat(form.lat || DEFAULT_CENTER[0]), parseFloat(val)]);
                                            }} step="any" placeholder="-61.5181" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div>
                        {/* Quick Note */}
                        <div className="admin-card" style={{ borderLeft: "4px solid #2a89ba" }}>
                            <div className="admin-card-body">
                                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                                    <i className="fa fa-info-circle" style={{ color: "#2a89ba", fontSize: 18, marginTop: 2 }} />
                                    <div style={{ fontSize: 13, lineHeight: 1.7, color: "#555" }}>
                                        <strong>Admin Ad Creation</strong><br />
                                        Ads created by admin are published immediately with no coin deduction.
                                        Default expiry is <strong>120 days</strong>.
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status & Settings */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-cog" /> Status & Settings</div>
                                <div className="admin-form-group">
                                    <div className="admin-toggle-group">
                                        <label className="admin-toggle">
                                            <input type="checkbox" checked={form.status}
                                                onChange={(e) => handleChange("status", e.target.checked)} />
                                            <span className="admin-toggle-slider" />
                                        </label>
                                        <span className="admin-toggle-label">Active</span>
                                    </div>
                                </div>
                                <div className="admin-form-group">
                                    <div className="admin-toggle-group">
                                        <label className="admin-toggle">
                                            <input type="checkbox" checked={form.featured}
                                                onChange={(e) => handleChange("featured", e.target.checked)} />
                                            <span className="admin-toggle-slider" />
                                        </label>
                                        <span className="admin-toggle-label">Featured Ad ⭐</span>
                                    </div>
                                </div>
                                <div className="admin-form-group" style={{ marginTop: 20 }}>
                                    <label>Expiry Date</label>
                                    <input type="date" className="admin-form-control" value={form.expireDate}
                                        onChange={(e) => handleChange("expireDate", e.target.value)} />
                                    <div className="admin-form-helper">Leave empty for default 120-day expiry</div>
                                </div>
                            </div>
                        </div>

                        {/* Country Visibility */}
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-form-section-title"><i className="fa fa-globe" /> Country Visibility</div>
                                {MOCK_COUNTRIES.map((country) => (
                                    <div key={country} className="admin-form-group" style={{ marginBottom: 8 }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontWeight: 400 }}>
                                            <input type="checkbox" className="admin-checkbox"
                                                checked={form.countryList.includes(country)}
                                                onChange={() => handleCountryToggle(country)} />
                                            {country}
                                        </label>
                                    </div>
                                ))}
                                {errors.countryList && <div className="admin-form-error">{errors.countryList}</div>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Action Bar */}
                <div className="admin-card" style={{ marginTop: 10 }}>
                    <div className="admin-card-body" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <button type="button" className="admin-btn admin-btn-outline" onClick={() => navigate("/admin/ads")}>
                            <i className="fa fa-arrow-left" /> Back to Ads
                        </button>
                        <div style={{ display: "flex", gap: 10 }}>
                            <button type="button" className="admin-btn admin-btn-outline" onClick={() => handleSubmit(null, true)} disabled={saving}>
                                <i className="fa fa-save" /> Save as Draft
                            </button>
                            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                                {saving ? <><i className="fa fa-spinner fa-spin" /> Publishing...</> : <><i className="fa fa-paper-plane" /> Publish Ad</>}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AdAddPage;
